import React from "react";
import { DateTime } from "luxon";
import range from "lodash/range";
import {
  Button,
  SvgIcon,
  SvgNames,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import { Card, CardContent, CardSectionDivider } from "../Card";
import { CircleIcon } from "./CircleIcon";
import {
  HEADER_DATE_FORMAT,
  INLINE_EMPTY_SLOTS_MESSAGE,
  SLOT_TIME_FORMAT,
  SLOTS_ERROR,
  SLOTS_ERROR_BUTTON,
  SLOTS_UNAVAILABLE_BUTTON,
  SLOTS_UNAVAILABLE_LINE_1,
  SLOTS_UNAVAILABLE_LINE_2,
} from "./constants";
import {
  Column,
  Slot,
  SlotCheckmark,
  SlotSkeleton,
  SlotTimeText,
  SlotsErrorWrapper,
  SlotsGrid,
  SlotsHeader,
  SlotsHeaderText,
  SlotsPagerControlLeft,
  SlotsPagerControlRight,
  SlotsUnavailableP,
  SlotsUnavailableWrapper,
  InlineEmptySlotsWrapper,
  InlineEmptySlotsMessage,
  SlotsCtaWrapper,
} from "./siteSurveySchedulingStyles";
import { toDateTimeInZone, toStartOfDayInZone } from "./slotPickerDateTime";
import { LinkHelpPhone } from "checkout/components/atoms/ContactLinks";
import { type SiteSurveySlot } from "checkout/api/siteSurvey";
import { SwrSuspense } from "utils/SwrSuspense";

type SiteSurveySlotPickerWarning = {
  message: string;
  testId?: string;
  showSupportPhone?: boolean;
};

type SiteSurveySlotPickerProps = {
  slotListTestId?: string;
  slotsRetryTestId: string;
  noSlotsTestId?: string;
  showUnavailableModalTestId?: string;
  columns: number;
  timeZoneId?: string;
  startDate: DateTime;
  incrementDate: () => void;
  decrementDate: () => void;
  slots: SiteSurveySlot[] | undefined;
  slotsLoading: boolean;
  slotsError: unknown;
  onRetrySlots: () => void;
  onShowErrorDetails?: () => void;
  selectedSlot?: SiteSurveySlot;
  onSelectSlot: (slot: SiteSurveySlot) => void;
  emptySlotsMode?: "inline" | "contactTeam";
  emptySlotsMessage?: string;
  slotPickerWarning?: SiteSurveySlotPickerWarning;
  onContactTeam?: () => void;
  borderRadius?: string;
  showStartTimeOnly?: boolean;
  onLoadSlots?: () => void;
  loadSlotsLabel?: string;
  loadSlotsTestId?: string;
};

const SiteSurveySlotPicker: React.FC<SiteSurveySlotPickerProps> = ({
  slotListTestId,
  slotsRetryTestId,
  noSlotsTestId,
  showUnavailableModalTestId,
  columns,
  timeZoneId,
  startDate,
  incrementDate,
  decrementDate,
  slots,
  slotsLoading,
  slotsError,
  onRetrySlots,
  onShowErrorDetails,
  selectedSlot,
  onSelectSlot,
  emptySlotsMode = "inline",
  emptySlotsMessage = INLINE_EMPTY_SLOTS_MESSAGE,
  slotPickerWarning,
  onContactTeam,
  borderRadius,
  showStartTimeOnly = false,
  onLoadSlots,
  loadSlotsLabel,
  loadSlotsTestId,
}) => {
  const columnStartDates = range(columns).map((i) =>
    toStartOfDayInZone(startDate, timeZoneId).plus({ days: i })
  );

  const slotsByDay = columnStartDates.map((columnStart) =>
    (slots ?? []).filter((slot) =>
      toDateTimeInZone(slot.startTime, timeZoneId).hasSame(columnStart, "day")
    )
  );

  const slotsEmpty = slotsByDay.every((s) => s.length === 0);
  const showSlotPickerWarning = Boolean(slotPickerWarning);
  const showLoadSlotsCta =
    !showSlotPickerWarning &&
    Boolean(onLoadSlots) &&
    !slotsLoading &&
    slots === undefined &&
    !slotsError;

  const SlotsErrorBlock = () => (
    <SlotsErrorWrapper>
      <CircleIcon name={SvgNames.WarningOutline} />
      <Typography style={{ textAlign: "center", textWrap: "balance" }}>
        {SLOTS_ERROR}
      </Typography>
      <Button
        color="secondary"
        data-testid={slotsRetryTestId}
        size="sm"
        onClick={onRetrySlots}
      >
        {SLOTS_ERROR_BUTTON}
      </Button>
      {onShowErrorDetails && (
        <Button color="tertiary" size="sm" onClick={onShowErrorDetails}>
          Show Details
        </Button>
      )}
    </SlotsErrorWrapper>
  );

  const SlotsUnavailableBlock = ({
    children,
    contactButton,
    messageTestId,
  }: {
    children: React.ReactNode;
    contactButton?: React.ReactNode;
    messageTestId?: string;
  }) => (
    <SlotsUnavailableWrapper>
      <CircleIcon name={SvgNames.WarningOutline} />
      <SlotsUnavailableP data-testid={messageTestId}>
        {children}
      </SlotsUnavailableP>
      {contactButton}
    </SlotsUnavailableWrapper>
  );

  return (
    <Card data-testid={slotListTestId} borderRadius={borderRadius}>
      <SlotsHeader role="heading">
        <SlotsPagerControlLeft
          aria-label="Previous Date"
          type="button"
          onClick={decrementDate}
        >
          <SvgIcon
            name={SvgNames.ChevronLeft}
            height="2em"
            color={tokens.BRAND_HERO_BLUE}
          />
        </SlotsPagerControlLeft>
        <SlotsPagerControlRight
          aria-label="Next Date"
          type="button"
          onClick={incrementDate}
        >
          <SvgIcon
            name={SvgNames.ChevronRight}
            height="2em"
            color={tokens.BRAND_HERO_BLUE}
          />
        </SlotsPagerControlRight>
        {columnStartDates.map((columnStart, i) => (
          <SlotsHeaderText key={`site-survey-slots-header-${i}`}>
            {columnStart.toLocaleString(HEADER_DATE_FORMAT)}
          </SlotsHeaderText>
        ))}
      </SlotsHeader>
      <CardSectionDivider />
      <CardContent role={showSlotPickerWarning ? undefined : "listbox"}>
        {showSlotPickerWarning && slotPickerWarning ? (
          <SlotsUnavailableBlock messageTestId={slotPickerWarning.testId}>
            {slotPickerWarning.message}
            {slotPickerWarning.showSupportPhone ? (
              <>
                {" "}
                <LinkHelpPhone linkColor={tokens.BLACK} />
              </>
            ) : null}
          </SlotsUnavailableBlock>
        ) : showLoadSlotsCta ? (
          <SlotsCtaWrapper>
            <Button
              color="secondary"
              size="sm"
              data-testid={loadSlotsTestId}
              onClick={onLoadSlots}
            >
              {loadSlotsLabel}
            </Button>
          </SlotsCtaWrapper>
        ) : (
          <SwrSuspense data={slots} isLoading={slotsLoading} error={slotsError}>
            <SwrSuspense.Loading>
              <SlotsGrid>
                {slotsByDay.map((_, i) => (
                  <Column key={`site-survey-slot-grid-skeleton-${i}`}>
                    <SlotSkeleton />
                    <SlotSkeleton />
                    <SlotSkeleton />
                  </Column>
                ))}
              </SlotsGrid>
            </SwrSuspense.Loading>
            <SwrSuspense.Error>
              <SlotsErrorBlock />
            </SwrSuspense.Error>
            <SwrSuspense.Data>
              {slotsEmpty &&
                (emptySlotsMode === "contactTeam" ? (
                  <SlotsUnavailableBlock
                    messageTestId={noSlotsTestId}
                    contactButton={
                      <Button
                        color="secondary"
                        data-testid={showUnavailableModalTestId}
                        size="sm"
                        onClick={onContactTeam}
                      >
                        {SLOTS_UNAVAILABLE_BUTTON}
                      </Button>
                    }
                  >
                    {SLOTS_UNAVAILABLE_LINE_1}
                    <br />
                    {SLOTS_UNAVAILABLE_LINE_2}
                  </SlotsUnavailableBlock>
                ) : (
                  <InlineEmptySlotsWrapper>
                    <InlineEmptySlotsMessage data-testid={noSlotsTestId}>
                      {emptySlotsMessage}
                    </InlineEmptySlotsMessage>
                  </InlineEmptySlotsWrapper>
                ))}
              {!slotsEmpty && (
                <SlotsGrid>
                  {slotsByDay.map((columnSlots, i) => (
                    <Column key={`site-survey-slots-col-${i}`}>
                      {columnSlots.map((s) => (
                        <Slot
                          key={s.startTime}
                          role="option"
                          aria-selected={s === selectedSlot ? "true" : "false"}
                          onClick={() => onSelectSlot(s)}
                        >
                          <SlotTimeText>
                            {toDateTimeInZone(s.startTime, timeZoneId)
                              .toLocaleString(SLOT_TIME_FORMAT)
                              .replace(/ /g, "\u00A0")}
                            {!showStartTimeOnly && (
                              <>
                                &nbsp;-{" "}
                                {toDateTimeInZone(s.endTime, timeZoneId)
                                  .toLocaleString(SLOT_TIME_FORMAT)
                                  .replace(/ /g, "\u00A0")}
                              </>
                            )}
                          </SlotTimeText>
                          {s === selectedSlot && (
                            <SlotCheckmark name={SvgNames.CheckFilled} />
                          )}
                        </Slot>
                      ))}
                    </Column>
                  ))}
                </SlotsGrid>
              )}
            </SwrSuspense.Data>
          </SwrSuspense>
        )}
      </CardContent>
    </Card>
  );
};

export { SiteSurveySlotPicker };
export type { SiteSurveySlotPickerProps, SiteSurveySlotPickerWarning };
